import React from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import {
  ContentBlock,
  DisplayConceptRef,
  DisplayProduct,
  InlineElement,
  StructuredListItem,
  ProductComparisonRow,
  TableCell as APITableCell,
  TableRow as APITableRow,
} from '@/lib/api';

import { ProductMiniCard } from '../ProductMiniCard';
import { useStructuredDocRender } from '../StructuredDocRenderContext';
import { InlineElementsRenderer } from './InlineElementRenderer';

interface ContentBlockRendererProps {
  block: ContentBlock;
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}

export const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({
  block,
  concepts,
  productDetails,
}) => {
  switch (block.type) {
    case 'paragraph':
      return (
        <ParagraphRenderer block={block} concepts={concepts} productDetails={productDetails} />
      );

    case 'heading':
      return <HeadingRenderer block={block} concepts={concepts} />;

    case 'list':
      return <ListRenderer block={block} concepts={concepts} productDetails={productDetails} />;

    case 'table':
      return <TableRenderer block={block} concepts={concepts} />;

    case 'product_comparison_table':
      return (
        <ProductComparisonTableRenderer
          rows={block.rows}
          concepts={concepts}
          productDetails={productDetails}
        />
      );

    case 'block_quote':
      return (
        <BlockQuoteRenderer block={block} concepts={concepts} productDetails={productDetails} />
      );

    case 'code_block':
      return <CodeBlockRenderer block={block} />;

    case 'thematic_break':
      return <Divider sx={{ borderTop: '1px solid var(--mui-palette-divider)', my: 2 }} />;

    default:
      return null;
  }
};

// Renders a paragraph with floating ProductMiniCards for products mentioned within.
// Deduplication happens at two levels:
// 1. Within-paragraph: we dedupes crefs and show one card per
// 2. Cross-paragraph: shouldShowFloatingCard() returns true only for the first-owner block
const ParagraphRenderer: React.FC<{
  block: ContentBlock & { type: 'paragraph'; inline_elements: InlineElement[] };
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}> = ({ block, concepts, productDetails }) => {
  const { shouldShowFloatingCard } = useStructuredDocRender();

  // Collect all product crefs from inline elements, deduped via Set
  const conceptRefs = Array.from(new Set(extractConceptRefs(block.inline_elements)));

  // Filter to products with images that this block owns (first mention in document)
  const productsToShow = conceptRefs
    .map((cref) => concepts.find((c) => c.cref === cref))
    .filter(
      (c): c is DisplayConceptRef =>
        !!c && c.is_product && !!c.preview_image && shouldShowFloatingCard(c.cref, block)
    );

  // Render cards inline with text (not in a Stack above) so float: right works for wraparound
  return (
    <Box sx={{ my: 1 }}>
      {productsToShow.map((concept) => {
        const product = productDetails.get(concept.cref);
        return (
          <ProductMiniCard
            key={concept.cref}
            concept={concept}
            product={product}
            variant="floating"
          />
        );
      })}
      <Typography component="p" variant="body1">
        <InlineElementsRenderer elements={block.inline_elements} concepts={concepts} />
      </Typography>
    </Box>
  );
};

const HeadingRenderer: React.FC<{
  block: { level: number; inline_elements: InlineElement[] };
  concepts: DisplayConceptRef[];
}> = ({ block, concepts }) => {
  const variant = `h${Math.min(block.level, 6)}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <Typography variant={variant} sx={{ my: 2 }}>
      <InlineElementsRenderer elements={block.inline_elements} concepts={concepts} />
    </Typography>
  );
};

const ListRenderer: React.FC<{
  block: { ordered: boolean; items: StructuredListItem[] };
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}> = ({ block, concepts, productDetails }) => {
  const ListTag = block.ordered ? 'ol' : 'ul';

  return (
    <Box component={ListTag} sx={{ my: 1, pl: 3 }}>
      {block.items.map((item, i) => (
        <ListItemRenderer key={i} item={item} concepts={concepts} productDetails={productDetails} />
      ))}
    </Box>
  );
};

const ListItemRenderer: React.FC<{
  item: StructuredListItem;
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}> = ({ item, concepts, productDetails }) => {
  return (
    <li>
      {item.blocks.map((block: ContentBlock, i: number) => (
        <ContentBlockRenderer
          key={i}
          block={block}
          concepts={concepts}
          productDetails={productDetails}
        />
      ))}
    </li>
  );
};

const TableRenderer: React.FC<{
  block: { headers: APITableRow; rows: APITableRow[] };
  concepts: DisplayConceptRef[];
}> = ({ block, concepts }) => {
  return (
    <Box sx={{ my: 2, overflowX: 'auto' }}>
      <Table size="small" sx={{ border: '1px solid var(--mui-palette-divider)' }}>
        <TableHead>
          <TableRow>
            {block.headers.cells.map((cell, i) => (
              <TableCell key={i} sx={{ fontWeight: 'bold' }}>
                <InlineElementsRenderer elements={cell.inline_elements} concepts={concepts} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {block.rows.map((row, i) => (
            <TableRow key={i}>
              {row.cells.map((cell, j) => (
                <TableCell key={j}>
                  <InlineElementsRenderer elements={cell.inline_elements} concepts={concepts} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ProductComparisonTableRenderer: React.FC<{
  rows: ProductComparisonRow[];
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}> = ({ rows, concepts, productDetails }) => {
  return (
    <Stack spacing={1.5} sx={{ my: 2 }}>
      {rows.map((row, index) => {
        const concept = concepts.find((c) => c.cref === row.product_cref);
        const product = productDetails.get(row.product_cref);

        if (!concept) return null;

        const displayConcept = {
          ...concept,
          display: row.product_name || concept.display,
        };

        return (
          <ProductMiniCard
            key={index}
            concept={displayConcept}
            product={product}
            variant="table"
            comments={row.comments}
          />
        );
      })}
    </Stack>
  );
};

const BlockQuoteRenderer: React.FC<{
  block: { blocks: ContentBlock[] };
  concepts: DisplayConceptRef[];
  productDetails: Map<string, DisplayProduct>;
}> = ({ block, concepts, productDetails }) => {
  return (
    <Box
      component="blockquote"
      sx={{
        borderLeft: (theme) => `${theme.spacing(0.5)} solid var(--mui-palette-divider)`,
        paddingLeft: 2,
        marginLeft: 0,
        marginRight: 0,
        marginY: 2,
        fontStyle: 'italic',
        color: 'var(--mui-palette-text-secondary)',
      }}
    >
      {block.blocks.map((nestedBlock, i) => (
        <ContentBlockRenderer
          key={i}
          block={nestedBlock}
          concepts={concepts}
          productDetails={productDetails}
        />
      ))}
    </Box>
  );
};

const CodeBlockRenderer: React.FC<{
  block: { language?: string; code: string };
}> = ({ block }) => {
  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: 'var(--mui-palette-action-hover)',
        padding: 2,
        borderRadius: 1,
        overflowX: 'auto',
        my: 2,
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }}
    >
      <code className={block.language ? `language-${block.language}` : ''}>{block.code}</code>
    </Box>
  );
};

function extractConceptRefs(elements: InlineElement[]): string[] {
  const refs: string[] = [];

  for (const element of elements) {
    if (element.type === 'concept_link') {
      refs.push(element.cref);
      refs.push(...extractConceptRefs(element.text));
    } else if ('content' in element && Array.isArray(element.content)) {
      refs.push(...extractConceptRefs(element.content));
    } else if ('text' in element && Array.isArray(element.text)) {
      refs.push(...extractConceptRefs(element.text));
    }
  }

  return refs;
}
